import React, { useMemo, useState } from "react";
import { viewHref } from "@shared/navigation/tabs/ViewsProvider";
import { useSearchParams, useNavigate } from "react-router-dom";
import { buildDetailPath } from "@shared/navigation/tabs/detailRoutes";
import GenericCard from "@ui-components/Card/GenericCard";
import {
    GenericTable,
    GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import type { ParsedFilter } from "@ui-components/Searchbar/GenericSearchbar";
import {
    DetailCardSkeleton,
    ServiceDetailsTableSkeleton,
} from "kontrolEdge/components/Shared/ServiceDetailsPageSkeleton";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { useAppSelector } from "@redux/hooks";
import {
    ErrorStateView,
    EmptyStateView,
} from "kontrolEdge/components/Backbone/BackboneDetails/StateViews";
import VmSpecsCard from "kontrolEdge/components/Shared/VmSpecsCard";
import { useSdwanHubDetails } from "./useSdwanHubDetails";
import type {
    SdwanAssociatedConnection,
    SdwanHubBranchRow,
} from "../sdwan.types";
import {
    StatusTag,
    InternetTagsCell,
    KvRow,
    fmtSpeed,
} from "../shared/sdwanFormat";

/** Searchable text per branch row (lowercased), keyed by column. */
const branchFields = (b: SdwanHubBranchRow): Record<string, string> => ({
    branch: `${b.subscription_id ?? ""} ${b.hostname} ${b.device_name}`.toLowerCase(),
    model: (b.model ?? "").toLowerCase(),
    location: (b.location ?? "").toLowerCase(),
    status: (b.status ?? "").toLowerCase(),
    ha: b.ha === "a-p" ? "active-passive a-p" : "standalone",
    primary: (b.internet_primary?.status ?? "").toLowerCase(),
    secondary: (b.internet_secondary?.status ?? "").toLowerCase(),
});

const SdwanHubDetails: React.FC = () => {
    const [searchParams] = useSearchParams();
    const sdwanId = searchParams.get("sdwanId");
    const navigate = useNavigate();
    const api = useAxiosAuth0();
    const [refresh, setRefresh] = useState(0);
    const [branchFilters, setBranchFilters] = useState<ParsedFilter[]>([]);
    const { tenantId } = useAppSelector((state) => state.selection);

    const { raw, loading, error, notProvisioned } = useSdwanHubDetails(
        sdwanId,
        api,
        refresh,
        tenantId ?? null
    );

    // Display label from the response. The list used to hand it over in `location.state`
    // too, which a reload or a pasted link loses; the label only renders once `raw` has
    // loaded, so the state never won anything (CTX-773).
    const serviceLabel = raw?.details.service_id ?? sdwanId ?? "";

    const branchColumns = useMemo<GenericTableColumn<SdwanHubBranchRow>[]>(
        () => [
            {
                header: "Branch",
                accessor: "hostname",
                sortable: true,
                renderCell: (row) => {
                    // Prefer the stable catalog id; fall back to the device
                    // name (pre-catalog / not yet scanned).
                    const ref = row.branch_id ?? row.device_name;
                    return (
                        <button
                            {...viewHref(buildDetailPath("sdwanBranch", { sdwanId, branchId: ref }))}
                            onClick={() =>
                                navigate(
                                    buildDetailPath("sdwanBranch", {
                                        sdwanId,
                                        branchId: ref,
                                    })
                                )
                            }
                            className="hover:text-semantic-text-brand hover:underline focus:underline active:underline font-medium underline text-semantic-text-brand"
                        >
                            {row.subscription_id ?? row.hostname}
                        </button>
                    );
                },
            },
            {
                header: "Model",
                accessor: "model",
                sortable: true,
                renderCell: (row) => row.model ?? "—",
            },
            {
                header: "Location",
                accessor: "location",
                sortable: true,
                renderCell: (row) => row.location ?? "—",
            },
            {
                header: "Status",
                accessor: "status",
                sortable: true,
                renderCell: (row) => <StatusTag status={row.status} />,
            },
            {
                id: "internet_primary",
                header: (
                    <div className="flex flex-col">
                        <span>Internet primary</span>
                        <span className="text-xs font-normal text-semantic-text-secondary">
                            Status / Interface
                        </span>
                    </div>
                ),
                renderCell: (row) => (
                    <InternetTagsCell entry={row.internet_primary} />
                ),
            },
            {
                id: "internet_secondary",
                header: (
                    <div className="flex flex-col">
                        <span>Internet secondary</span>
                        <span className="text-xs font-normal text-semantic-text-secondary">
                            Status / Interface
                        </span>
                    </div>
                ),
                renderCell: (row) => (
                    <InternetTagsCell entry={row.internet_secondary} />
                ),
            },
            {
                header: "HA",
                accessor: "ha",
                sortable: true,
                renderCell: (row) =>
                    row.ha === "a-p" ? "Active-Passive" : "Standalone",
            },
        ],
        [navigate, sdwanId]
    );

    // Branch search — filter in the parent (GenericTable renders pre-filtered
    // data). Free text matches any column; `key:value` (e.g. "location:gdl")
    // matches that column. Case-insensitive.
    const filteredBranches = useMemo(() => {
        const rows = raw?.branches ?? [];
        if (!branchFilters.length) return rows;
        return rows.filter((b) => {
            const fields = branchFields(b);
            const all = Object.values(fields).join(" ");
            return branchFilters.every((f) => {
                const v = f.value.trim().toLowerCase();
                if (!v) return true;
                const key = f.key?.trim().toLowerCase();
                return key && key in fields
                    ? fields[key].includes(v)
                    : all.includes(v);
            });
        });
    }, [raw?.branches, branchFilters]);

    // Associated Internet connections (like the Port "Associated Connections"
    // table — shows only the internet tied to this SD-WAN hub).
    const connectionColumns = useMemo<
        GenericTableColumn<SdwanAssociatedConnection>[]
    >(
        () => [
            {
                header: "Connection Id",
                sortable: true,
                sortAccessor: (row) => row.subscription_id ?? "",
                renderCell: (row) => (
                    <button
                        {...viewHref(buildDetailPath("internet", { connectionId: row.id }))}
                        onClick={() =>
                            navigate(buildDetailPath("internet", { connectionId: row.id }))
                        }
                        className="hover:text-semantic-text-brand hover:underline focus:underline active:underline font-medium underline text-semantic-text-brand"
                    >
                        {row.subscription_id ?? `#${row.id}`}
                    </button>
                ),
            },
            {
                header: "Type",
                accessor: "type",
                sortable: true,
            },
            {
                header: "Bandwidth",
                sortAccessor: (row) => row.mbps ?? -1,
                sortable: true,
                renderCell: (row) => fmtSpeed(row.mbps),
            },
            {
                header: "VLAN",
                renderCell: (row) => row.vlan ?? "—",
            },
            {
                header: "Routing",
                renderCell: (row) => row.routing ?? "—",
            },
        ],
        [navigate]
    );

    if (loading) {
        return (
            <div
                className="flex flex-col gap-4 py-4"
                role="status"
                aria-busy
                aria-label="Loading SD-WAN hub details"
            >
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <DetailCardSkeleton />
                    <DetailCardSkeleton />
                    <DetailCardSkeleton />
                </div>
                <ServiceDetailsTableSkeleton
                    variant="data"
                    rows={5}
                    columns={7}
                />
                <ServiceDetailsTableSkeleton
                    variant="data"
                    rows={2}
                    columns={5}
                />
            </div>
        );
    }
    if (notProvisioned)
        return (
            <EmptyStateView
                title="SD-WAN service not fully provisioned"
                description="This SD-WAN service isn't fully set up yet. Its details will show here once provisioning is complete."
            />
        );
    if (error)
        return (
            <ErrorStateView
                title="Failed to load SD-WAN service"
                message={error}
                onRetry={() => setRefresh((r) => r + 1)}
            />
        );
    if (!raw)
        return (
            <EmptyStateView
                title="SD-WAN service not found"
                description="We couldn't load this SD-WAN service. It may have been removed or you may not have access."
            />
        );

    const d = raw.details;
    const s = raw.status;

    return (
        <div className="flex flex-col gap-4">
            {/* Back navigation is provided by the SideNav (getBackRoute). */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <GenericCard isCollapsible={false} variant="primary" shadow="sm">
                    <div className="p-5">
                        <h3 className="font-semibold mb-3">Hub details</h3>
                        <KvRow label="Service ID">{serviceLabel}</KvRow>
                        <KvRow label="Datacenter">{d.datacenter ?? "—"}</KvRow>
                        <KvRow label="Connected branches">
                            {`${s.connected_branches.up} / ${s.connected_branches.total}`}
                        </KvRow>
                    </div>
                </GenericCard>

                <GenericCard isCollapsible={false} variant="primary" shadow="sm">
                    <div className="p-5">
                        <h3 className="font-semibold mb-3">Status</h3>
                        <KvRow label="State">
                            {s.vm_state ? (
                                <StatusTag status={s.vm_state} />
                            ) : (
                                "—"
                            )}
                        </KvRow>
                        <KvRow label="SD-WAN service">
                            {s.sdwan_service ? (
                                <StatusTag status={s.sdwan_service} />
                            ) : (
                                "—"
                            )}
                        </KvRow>
                    </div>
                </GenericCard>

                {/* The Virtual Firewall detail's card: one component for both services. */}
                <VmSpecsCard vm={raw.vm ?? null} />
            </div>

            <GenericCard isCollapsible={false} variant="primary" shadow="sm">
                <div className="p-5">
                    <h3 className="font-semibold">Associated branches</h3>
                    <p className="text-sm text-semantic-text-secondary mb-3">
                        Sites connected to this hub
                    </p>
                    <div className="flex items-center gap-4 mb-4">
                        <div className="w-[520px] max-w-full">
                            <GenericSearchbar
                                placeholder="Find by branch, location, model, status, primary, secondary…"
                                suggestedKeys={[
                                    "branch",
                                    "location",
                                    "model",
                                    "status",
                                    "ha",
                                    "primary",
                                    "secondary",
                                ]}
                                inputMaxWidth="100%"
                                onFiltersChange={setBranchFilters}
                            />
                        </div>
                    </div>
                    <GenericTable
                        columns={branchColumns}
                        // A phone reads each branch in two lines: ID and
                        // status, then model, site, HA and its internet links
                        // (CTX-1479).
                        mobileCards={{
                            titleColumn: "Branch",
                            badgeColumn: "Status",
                            summary: (row) => [
                                row.model,
                                row.location,
                                row.ha === "a-p" ? "Active-Passive" : "Standalone",
                                row.internet_primary &&
                                    `Primary ${row.internet_primary.status}`,
                                row.internet_secondary &&
                                    `Secondary ${row.internet_secondary.status}`,
                            ],
                            opensFromTitle: true,
                            sortControl: true,
                            itemNoun: "branch",
                        }}
                        data={filteredBranches}
                        initialRowsPerPage={10}
                        rowsPerPageOptions={[10, 20, 30]}
                        width="100%"
                        tableClassName="bg-semantic-bg-primary"
                    />
                </div>
            </GenericCard>

            <GenericCard isCollapsible={false} variant="primary" shadow="sm">
                <div className="p-5">
                    <h3 className="font-semibold">Associated Connections</h3>
                    <p className="text-sm text-semantic-text-secondary mb-3">
                        Internet connections tied to this SD-WAN hub
                    </p>
                    {raw.associated_connections &&
                    raw.associated_connections.length ? (
                        <GenericTable
                            columns={connectionColumns}
                            mobileCards={{
                                titleColumn: "Connection Id",
                                summary: (row) => [
                                    row.type,
                                    row.mbps != null ? fmtSpeed(row.mbps) : null,
                                    row.vlan != null ? `VLAN ${row.vlan}` : null,
                                    row.routing,
                                ],
                                opensFromTitle: true,
                            }}
                            data={raw.associated_connections}
                            initialRowsPerPage={5}
                            rowsPerPageOptions={[5, 10, 20]}
                            width="100%"
                            tableClassName="bg-semantic-bg-primary"
                        />
                    ) : (
                        <p className="text-sm text-semantic-text-secondary">
                            No internet connections associated with this hub.
                        </p>
                    )}
                </div>
            </GenericCard>
        </div>
    );
};

export default SdwanHubDetails;
